import { listSchoolOrganizationsAll } from 'util/listSchoolOrganizationsAll';
import { useState, useEffect } from 'react';
import { useOrganizationsApi, useSchoolsApi, OrganizationSummary } from 'services/api';
import useSWR from 'swr';

// Custom hook to fetch organization data
export const useOrganizationData = (open: boolean, selectedOrganizationId: string) => {
  const organizationsApi = useOrganizationsApi();
  const rOrganization = useSWR(
    open && selectedOrganizationId
      ? [organizationsApi.getOrganization, selectedOrganizationId]
      : null,
    async () => {
      const result = await organizationsApi.getOrganization(selectedOrganizationId);
      if (result.success) return result.value;
      throw new Error(result.error.message);
    },
  );
  return rOrganization;
};

// Custom hook to fetch school data.
//
// We only fetch once the organization has loaded and we have the authoritative
// schoolId (a UUID). The `schoolSlug` prop is only used as a hint: if the
// caller passes a slug that doesn't match an existing school (e.g. the event
// and its host-org belong to different schools), a slug lookup would produce
// a noisy "No school found" 404 in the API logs. The schoolId path always
// resolves correctly for any org the user is allowed to view.
export const useSchoolData = (
  open: boolean,
  _schoolSlug: string | undefined,
  schoolIdFromOrg: string | undefined,
) => {
  const schoolsApi = useSchoolsApi();
  const rSchool = useSWR(
    open && schoolIdFromOrg
      ? [schoolsApi.getSchool, schoolIdFromOrg]
      : null,
    async () => {
      if (!schoolIdFromOrg) return undefined;
      const result = await schoolsApi.getSchool(schoolIdFromOrg);
      if (result.success) return result.value;
      // Don't throw — the school details are only used for optional fields
      // (terms URL, display name). Treating access/not-found failures as
      // "no extra details available" keeps the modal functional.
      return undefined;
    },
    { shouldRetryOnError: false },
  );
  return rSchool;
};

// Custom hook to fetch available organizations
export const useAvailableOrganizations = (open: boolean, schoolId: string | undefined) => {
  const schoolsApi = useSchoolsApi();
  const [availableOrganizations, setAvailableOrganizations] = useState<OrganizationSummary[]>([]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!open || !schoolId) return;

      try {
        const allOrganizations = await listSchoolOrganizationsAll(
          schoolsApi,
          String(schoolId),
        );
        setAvailableOrganizations(allOrganizations);
      } catch (error) {
        // Silently fail - organizations dropdown will just not show
      }
    };

    void fetchOrganizations();
  }, [open, schoolId, schoolsApi]);

  return availableOrganizations;
};

// Custom hook to fetch events
export const useOrganizationEvents = (open: boolean, selectedOrganizationId: string) => {
  const organizationsApi = useOrganizationsApi();
  const rEvents = useSWR(
    open && selectedOrganizationId
      ? [
          `/organizations/${selectedOrganizationId}/events`,
          ['PUBLISHED', 'UPDATED', 'RESCHEDULED'],
          'UPCOMING',
        ]
      : null,
    async () => {
      const result = await organizationsApi.listOrganizationEvents(
        selectedOrganizationId,
        {
          statuses: ['PUBLISHED', 'UPDATED', 'RESCHEDULED'],
          displayFilter: 'UPCOMING',
        },
      );
      if (result.success)
        return result.value;

      throw new Error(result.error.message);
    },
  );
  return rEvents;
};
